import React, { useEffect } from 'react';
import Layout from "../temp/layout/Layout";
import { Helmet } from 'react-helmet';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { Link } from 'react-router-dom';
import { 
    FaCalendarAlt, FaUser, FaArrowRight, FaTag, FaClock,
    FaGraduationCap, FaLaptopCode, FaChartLine, FaBook
} from 'react-icons/fa';

const Blog = () => {
    useEffect(() => {
        AOS.init({ 
            duration: 1000, 
            once: true,
            offset: 100
        });
    }, []);

    const blogPosts = [
        {
            id: 1,
            title: "10 Benefits of Digital Transformation in Education",
            excerpt: "Discover how digital transformation is revolutionizing the education sector and improving learning outcomes.",
            author: "Educational Eternity Team",
            date: "March 15, 2024",
            category: "Digital Transformation",
            readTime: "5 min read",
            image: "assets/images/newai.jpeg",
            color: "from-blue-500 to-indigo-600"
        },
        {
            id: 2,
            title: "How AI is Personalizing Student Learning",
            excerpt: "Explore how artificial intelligence is creating personalized learning experiences for students.",
            author: "Educational Eternity Team",
            date: "March 10, 2024",
            category: "AI & Technology",
            readTime: "7 min read",
            image: "assets/images/WhatWeOffer.jpeg",
            color: "from-purple-500 to-pink-600"
        },
        {
            id: 3,
            title: "Streamlining School Administration with ERP Systems",
            excerpt: "Learn how ERP systems can help schools manage operations more efficiently and reduce administrative burden.",
            author: "Educational Eternity Team",
            date: "March 5, 2024",
            category: "School Management",
            readTime: "6 min read",
            image: "assets/images/about1.jpeg",
            color: "from-green-500 to-teal-600"
        },
        {
            id: 4,
            title: "The Future of Online Learning Platforms",
            excerpt: "Understanding the trends and technologies shaping the future of online education and virtual classrooms.",
            author: "Educational Eternity Team",
            date: "February 28, 2024",
            category: "Online Learning",
            readTime: "8 min read",
            image: "assets/images/newabout.jpg",
            color: "from-orange-500 to-red-600"
        },
        {
            id: 5,
            title: "Best Practices for Student Data Management",
            excerpt: "Essential strategies for securely managing and protecting student data in educational institutions.",
            author: "Educational Eternity Team",
            date: "February 20, 2024",
            category: "Data Management",
            readTime: "5 min read",
            image: "assets/images/Aboutnew.jpeg",
            color: "from-cyan-500 to-blue-600"
        },
        {
            id: 6,
            title: "Improving Parent-Teacher Communication",
            excerpt: "How technology is bridging the communication gap between parents and teachers for better student outcomes.",
            author: "Educational Eternity Team",
            date: "February 15, 2024",
            category: "Communication",
            readTime: "4 min read",
            image: "assets/images/about12.jpeg",
            color: "from-pink-500 to-rose-600"
        }
    ];

    const categories = ["All", "Digital Transformation", "AI & Technology", "School Management", "Online Learning", "Data Management", "Communication"];

    return (
        <Layout>
            <Helmet>
                <title>Blog & Resources - Educational Eternity | Education Technology Insights</title>
                <meta name="description" content="Read our latest blog posts about education technology, digital transformation, AI in education, and school management best practices." />
            </Helmet>

            {/* Hero Section */}
            <section className="relative w-full h-[50vh] bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 opacity-20" style={{
                    backgroundImage: `url(assets/images/home1.jpg)`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}></div>
                <div className="relative z-10 text-center px-4">
                    <h1 data-aos="fade-up" className="text-5xl md:text-7xl font-bold text-white mb-4">
                        Blog & Resources
                    </h1>
                    <p data-aos="fade-up" data-aos-delay="100" className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
                        Insights, tips, and trends in education technology
                    </p>
                </div>
            </section>

            {/* Category Filter */}
            <section className="py-6 bg-white border-b border-gray-200 sticky top-16 z-30 shadow-sm">
                <div className="container mx-auto px-4">
                    <div className="flex flex-wrap justify-center gap-3">
                        {categories.map((category) => (
                            <button
                                key={category}
                                className="px-5 py-2 rounded-full font-semibold transition-all duration-300 bg-gray-100 text-gray-700 hover:bg-gray-200"
                            >
                                {category}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Blog Posts Grid */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {blogPosts.map((post, idx) => (
                            <article
                                key={post.id}
                                data-aos="fade-up"
                                data-aos-delay={idx * 100}
                                className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden group"
                            >
                                {/* Image */}
                                <div className="relative h-48 overflow-hidden">
                                    <div className={`absolute inset-0 bg-gradient-to-br ${post.color} opacity-90`}></div>
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <FaBook className="text-white text-6xl opacity-50" />
                                    </div>
                                    <div className="absolute top-4 left-4">
                                        <span className="bg-white/90 backdrop-blur-md px-3 py-1 rounded-full text-xs font-bold text-gray-800">
                                            {post.category}
                                        </span>
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="p-6">
                                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
                                        {post.title}
                                    </h3>
                                    <p className="text-gray-600 mb-4 line-clamp-3">
                                        {post.excerpt}
                                    </p>

                                    {/* Meta Info */}
                                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                                        <div className="flex items-center gap-2">
                                            <FaUser className="text-gray-400" />
                                            <span>{post.author}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <FaClock className="text-gray-400" />
                                            <span>{post.readTime}</span>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2 text-sm text-gray-500">
                                            <FaCalendarAlt />
                                            <span>{post.date}</span>
                                        </div>
                                        <button className="flex items-center gap-2 text-blue-600 font-semibold hover:text-blue-700 transition-colors">
                                            Read More <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
                                        </button>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600">
                <div className="container mx-auto px-4 text-center" data-aos="fade-up">
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                        Stay Updated with Latest Insights
                    </h2>
                    <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
                        Subscribe to our newsletter to receive the latest articles, tips, and updates about education technology.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            className="flex-1 px-6 py-4 rounded-full text-gray-900 focus:outline-none focus:ring-2 focus:ring-white"
                        />
                        <button className="px-8 py-4 bg-white text-blue-600 rounded-full font-bold hover:bg-blue-50 transform hover:scale-105 transition-all duration-300">
                            Subscribe
                        </button>
                    </div>
                </div>
            </section>
        </Layout>
    );
};

export default Blog;

